import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type LoaderState = {
  [id: string]: boolean;
};

type SkillAreasThresholdRequestTrackerContextType = {
  showProgressBars: boolean;
  registerLoader: (id: string) => void;
  setLoaderComplete: (id: string) => void;
};

const SkillAreasThresholdRequestTrackerContext = createContext<SkillAreasThresholdRequestTrackerContextType>({
  showProgressBars: false,
  registerLoader: () => {},
  setLoaderComplete: () => {},
});

export const useSkillAreasThresholdRequestTracker = () => useContext(SkillAreasThresholdRequestTrackerContext);

type Props = {
  children: React.ReactNode;
};

export const SkillAreasThresholdRequestTrackerProvider = ({ children }: Props) => {
  const loadersRef = useRef<LoaderState>({});
  const [showProgressBars, setShowProgressBars] = useState(false);
  const checkScheduledRef = useRef(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, []);

  const checkAllLoadersComplete = useCallback<() => void>(() => {
    const loaders = loadersRef.current;
    const loaderIds = Object.keys(loaders);

    if (loaderIds.length === 0) {
      return;
    }

    const allComplete = !loaderIds.some((id) => loaders[id] === false);

    if (allComplete) {
      setShowProgressBars(true);
    }
  }, []);

  const scheduleCheck = useCallback<() => void>(() => {
    if (checkScheduledRef.current) {
      return;
    }
    checkScheduledRef.current = true;

    // Use setTimeout to defer the check until after all components have mounted
    // and registered their loaders. This ensures we wait for the next event loop tick,
    // giving all useEffect hooks time to run.
    timeoutIdRef.current = setTimeout(() => {
      checkScheduledRef.current = false;
      timeoutIdRef.current = null;
      checkAllLoadersComplete();
    }, 0);
  }, [checkAllLoadersComplete]);

  const registerLoader = useCallback<(id: string) => void>((id) => {
    loadersRef.current[id] = false;
  }, []);

  const setLoaderComplete = useCallback<(id: string) => void>(
    (id) => {
      loadersRef.current[id] = true;
      scheduleCheck();
    },
    [scheduleCheck]
  );

  const value = useMemo(
    () => ({
      showProgressBars,
      registerLoader,
      setLoaderComplete,
    }),
    [showProgressBars, registerLoader, setLoaderComplete]
  );

  return (
    <SkillAreasThresholdRequestTrackerContext.Provider value={value}>
      {children}
    </SkillAreasThresholdRequestTrackerContext.Provider>
  );
};
